import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PremierUpgradeModal } from '../components/PremierUpgradeModal';
import { Upload, Trash2, Image as ImageIcon, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  email: '', phone: '', website: '', street_address: '', city: '', state: '', zip_code: '',
  bio: '', years_experience: '', hourly_rate: '', commission_rate: '', service_radius_miles: 50,
  specialties: '', certifications: '', marketing_tagline: '', video_url: '',
  facebook_url: '', instagram_url: '', linkedin_url: '', twitter_url: '', tiktok_url: '', youtube_url: '',
};

export const IndividualAuctioneerDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const logoRef = useRef(null);
  const galleryRef = useRef(null);

  const token = () => localStorage.getItem('token');

  useEffect(() => {
    if (!token()) { navigate('/auctioneer/individual/login'); return; }
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/api/auctioneers/profile/me`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token'); localStorage.removeItem('user');
          navigate('/auctioneer/individual/login'); return;
        }
        throw new Error('Failed to fetch profile');
      }
      const data = await response.json();
      const p = data.profile;
      setProfile(p);
      setGallery(p.gallery_images || []);
      setFormData({
        email: p.email || '', phone: p.phone || '', website: p.website || '',
        street_address: p.street_address || '', city: p.city || '', state: p.state || '', zip_code: p.zip_code || p.service_zip || '',
        bio: p.bio || '', years_experience: p.years_experience ?? '', hourly_rate: p.hourly_rate || '',
        commission_rate: p.commission_rate || '', service_radius_miles: p.service_radius_miles || 50,
        specialties: (p.specialties || []).join(', '), certifications: (p.certifications || []).join(', '),
        marketing_tagline: p.marketing_tagline || '', video_url: p.video_url || '',
        facebook_url: p.facebook_url || '', instagram_url: p.instagram_url || '', linkedin_url: p.linkedin_url || '',
        twitter_url: p.twitter_url || '', tiktok_url: p.tiktok_url || '', youtube_url: p.youtube_url || '',
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const f = formData;
    const payload = {};
    const strFields = ['email', 'phone', 'website', 'street_address', 'city', 'state', 'zip_code', 'bio',
      'hourly_rate', 'commission_rate', 'marketing_tagline', 'video_url',
      'facebook_url', 'instagram_url', 'linkedin_url', 'twitter_url', 'tiktok_url', 'youtube_url'];
    strFields.forEach((k) => { if (f[k] !== undefined && f[k] !== '') payload[k] = f[k]; });
    if (f.state) payload.state = f.state.toUpperCase();
    if (f.years_experience !== '' && f.years_experience !== null) payload.years_experience = parseInt(f.years_experience, 10);
    if (f.service_radius_miles !== '' && f.service_radius_miles !== null) payload.service_radius_miles = parseInt(f.service_radius_miles, 10);
    payload.specialties = f.specialties ? f.specialties.split(',').map((s) => s.trim()).filter(Boolean) : [];
    payload.certifications = f.certifications ? f.certifications.split(',').map((s) => s.trim()).filter(Boolean) : [];
    return payload;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveSuccess(false); setError('');
    try {
      const response = await fetch(`${API}/api/auctioneers/profile/me`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      if (!response.ok) {
        const d = await response.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to update profile');
      }
      setSaveSuccess(true); setEditing(false); fetchProfile();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) { setError(err.message); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/api/auctioneers/profile/me/upload-logo`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Logo upload failed');
      fetchProfile();
    } catch (err) { setError(err.message); }
    finally { setLogoUploading(false); if (logoRef.current) logoRef.current.value = ''; }
  };

  const uploadGalleryImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGalleryUploading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/api/auctioneers/profile/me/gallery`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Image upload failed');
      setGallery((g) => [...g, d.url]);
    } catch (err) { setError(err.message); }
    finally { setGalleryUploading(false); if (galleryRef.current) galleryRef.current.value = ''; }
  };

  const deleteGalleryImage = async (url) => {
    try {
      const res = await fetch(`${API}/api/auctioneers/profile/me/gallery?url=${encodeURIComponent(url)}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      setGallery((g) => g.filter((u) => u !== url));
    } catch (err) { setError(err.message); }
  };

  const handleLogout = () => {
    localStorage.removeItem('token'); localStorage.removeItem('user');
    navigate('/auctioneer/individual/login');
  };

  const field = (label, key, type = 'text', placeholder = '') => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type={type} value={formData[key]} placeholder={placeholder} data-testid={`field-${key}`}
        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
    </div>
  );

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading profile...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8" data-testid="individual-dashboard">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                {profile?.logo_url && (
                  <img src={`${API}${profile.logo_url}`} alt="logo" data-testid="header-logo"
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200" />
                )}
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold text-gray-900">{profile?.name}</h1>
                    {profile?.is_premier && (
                      <span className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-sm font-bold rounded-full">PREMIER</span>
                    )}
                  </div>
                  <p className="text-gray-600">{profile?.city}, {profile?.state}</p>
                  {profile?.license_number && <p className="text-sm text-gray-500">License: {profile.license_number}</p>}
                </div>
              </div>
              <button onClick={handleLogout} data-testid="dashboard-logout-btn"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition">Logout</button>
            </div>
          </div>

          {/* Premier banner / status */}
          {!profile?.is_premier ? (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg p-6 mb-6 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Upgrade to Premier Listing</h3>
              <p className="text-gray-700 mb-4">Top search position, custom logo &amp; video, AI search visibility, and a verified Premier badge.</p>
              <div className="flex items-center gap-4">
                <button onClick={() => setShowUpgradeModal(true)} data-testid="upgrade-premier-btn"
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-md transition">
                  Upgrade Now - Only $4.99/month
                </button>
                <span className="text-sm text-gray-600">Billed annually at $59.88</span>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-400 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-1">You're a Premier Member!</h3>
              <p className="text-gray-700">Your listing appears at the top of search results with enhanced visibility.</p>
              {profile?.premier_expires_at && (
                <p className="text-sm text-gray-600 mt-2">Active until: {new Date(profile.premier_expires_at).toLocaleDateString()}</p>
              )}
            </div>
          )}

          {saveSuccess && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="save-success">
              <p className="text-green-700">Profile updated successfully!</p>
            </div>
          )}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="dashboard-error">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Branding: logo + gallery */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Branding &amp; Media</h2>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                  {profile?.logo_url
                    ? <img src={`${API}${profile.logo_url}`} alt="logo" className="w-full h-full object-cover" />
                    : <ImageIcon className="w-8 h-8 text-gray-300" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Business Logo</p>
                  <p className="text-xs text-gray-500 mb-2">JPG/PNG/WebP, max 2MB</p>
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
                  <button onClick={() => logoRef.current?.click()} disabled={logoUploading} data-testid="upload-logo-btn"
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition disabled:opacity-50">
                    {logoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload logo
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Photo Gallery <span className="text-sm font-normal text-gray-500">({gallery.length}/12)</span></h3>
              <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={uploadGalleryImage} data-testid="gallery-file-input" />
              <button onClick={() => galleryRef.current?.click()} disabled={galleryUploading || gallery.length >= 12} data-testid="upload-gallery-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition disabled:opacity-50">
                {galleryUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Add image
              </button>
            </div>
            {gallery.length === 0 ? (
              <p className="text-sm text-gray-500">No images yet. Showcase your auctions, venue, or team.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="gallery-grid">
                {gallery.map((url) => (
                  <div key={url} className="relative group rounded-lg overflow-hidden border border-gray-200 aspect-square">
                    <img src={`${API}${url}`} alt="gallery" className="w-full h-full object-cover" />
                    <button onClick={() => deleteGalleryImage(url)} data-testid="delete-gallery-img"
                      className="absolute top-1 right-1 p-1.5 bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Profile info */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Profile Information</h2>
              {!editing && (
                <button onClick={() => setEditing(true)} data-testid="edit-profile-btn"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition">Edit Profile</button>
              )}
            </div>

            {editing ? (
              <form onSubmit={handleSave} className="space-y-6" data-testid="profile-edit-form">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact &amp; Location</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {field('Email', 'email', 'email')}
                    {field('Phone', 'phone', 'tel')}
                    {field('Website', 'website', 'url', 'https://example.com')}
                    {field('Street Address', 'street_address')}
                    {field('City', 'city')}
                    {field('State', 'state', 'text', 'e.g. FL')}
                    {field('ZIP Code', 'zip_code')}
                    {field('Service Radius (miles)', 'service_radius_miles', 'number')}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Business Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {field('Years of Experience', 'years_experience', 'number')}
                    {field('Hourly Rate', 'hourly_rate', 'text', '$150/hour')}
                    {field('Commission Rate', 'commission_rate', 'text', 'e.g. 10%')}
                    {field('Marketing Tagline', 'marketing_tagline')}
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Specialties <span className="text-gray-400">(comma separated)</span></label>
                    <input type="text" value={formData.specialties} data-testid="field-specialties"
                      onChange={(e) => setFormData({ ...formData, specialties: e.target.value })}
                      placeholder="Estate Sales, Real Estate, Fine Art"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Certifications <span className="text-gray-400">(comma separated)</span></label>
                    <input type="text" value={formData.certifications} data-testid="field-certifications"
                      onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                      placeholder="CAI, BAS, State Licensed"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    <textarea rows="4" value={formData.bio} data-testid="field-bio"
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      placeholder="Tell potential clients about your experience and services..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Video &amp; Social</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {field('Video URL (YouTube/Vimeo)', 'video_url', 'url', 'https://youtube.com/watch?v=...')}
                    {field('YouTube', 'youtube_url', 'url')}
                    {field('Facebook', 'facebook_url', 'url')}
                    {field('Instagram', 'instagram_url', 'url')}
                    {field('LinkedIn', 'linkedin_url', 'url')}
                    {field('Twitter/X', 'twitter_url', 'url')}
                    {field('TikTok', 'tiktok_url', 'url')}
                  </div>
                </div>

                <div className="flex gap-3 pt-2 border-t">
                  <button type="submit" data-testid="save-profile-btn"
                    className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition">Save Changes</button>
                  <button type="button" onClick={() => { setEditing(false); fetchProfile(); }}
                    className="px-6 py-2 bg-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-400 transition">Cancel</button>
                </div>
              </form>
            ) : (
              <div className="space-y-4" data-testid="profile-view">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><p className="text-sm text-gray-500">Email</p><p className="font-medium">{profile?.email || 'Not set'}</p></div>
                  <div><p className="text-sm text-gray-500">Phone</p><p className="font-medium">{profile?.phone || 'Not set'}</p></div>
                  <div><p className="text-sm text-gray-500">Website</p><p className="font-medium">{profile?.website || 'Not set'}</p></div>
                  <div><p className="text-sm text-gray-500">Years of Experience</p><p className="font-medium">{profile?.years_experience || 'Not set'}</p></div>
                  <div><p className="text-sm text-gray-500">Service Radius</p><p className="font-medium">{profile?.service_radius_miles || 50} miles</p></div>
                  <div><p className="text-sm text-gray-500">Hourly Rate</p><p className="font-medium">{profile?.hourly_rate || 'Not set'}</p></div>
                  <div><p className="text-sm text-gray-500">Commission</p><p className="font-medium">{profile?.commission_rate || 'Not set'}</p></div>
                  <div><p className="text-sm text-gray-500">Video</p><p className="font-medium truncate">{profile?.video_url || 'Not set'}</p></div>
                </div>
                {(profile?.specialties?.length > 0) && (
                  <div><p className="text-sm text-gray-500 mb-1">Specialties</p><p className="text-gray-700">{profile.specialties.join(', ')}</p></div>
                )}
                {(profile?.certifications?.length > 0) && (
                  <div><p className="text-sm text-gray-500 mb-1">Certifications</p><p className="text-gray-700">{profile.certifications.join(', ')}</p></div>
                )}
                {profile?.bio && (
                  <div><p className="text-sm text-gray-500 mb-1">Bio</p><p className="text-gray-700">{profile.bio}</p></div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <PremierUpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} onUpgrade={() => { fetchProfile(); setShowUpgradeModal(false); }} />
      <Footer />
    </>
  );
};
